import { supabase } from './supabase.js';
import { coerceUrl } from './url.js';

const SELECT_COLUMNS = [
  'id',
  'profile_id',
  'name',
  'position',
  'department',
  'university',
  'website',
  'email_public',
  'city',
  'country',
  'bibliography_link',
  'status',
  'submitted_by',
  'created_at',
  'updated_at',
].join(', ');

export const EMPTY_SCHOLAR = {
  name: '',
  position: '',
  department: '',
  university: '',
  website: '',
  email_public: '',
  city: '',
  country: '',
  bibliography_link: '',
};

export const EMPTY_SCHOLAR_FILTERS = {
  country: '',
  university: '',
};

// Public list of approved scholars. Anon can see status='published' rows.
export async function fetchPublishedScholars() {
  const { data, error } = await supabase
    .from('scholars')
    .select(SELECT_COLUMNS)
    .eq('status', 'published')
    .order('name', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function fetchScholar(id) {
  const { data, error } = await supabase
    .from('scholars')
    .select(SELECT_COLUMNS)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

// Find the scholar row owned by the current authenticated user, if any.
// Used to drive the "Edit my profile" affordance and prevent duplicate submissions.
export async function fetchMyScholar(profileId) {
  if (!profileId) return null;
  const { data, error } = await supabase
    .from('scholars')
    .select(SELECT_COLUMNS)
    .eq('profile_id', profileId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

function normalize(form) {
  const blank = (v) => (v == null || v === '' ? null : v);
  return {
    name: form.name?.trim(),
    position: blank(form.position?.trim?.()),
    department: blank(form.department?.trim?.()),
    university: blank(form.university?.trim?.()),
    website: coerceUrl(form.website),
    email_public: blank(form.email_public?.trim?.()),
    city: blank(form.city?.trim?.()),
    country: blank(form.country?.trim?.()),
    bibliography_link: coerceUrl(form.bibliography_link),
  };
}

export function validateScholar(form) {
  if (!form.name?.trim()) return 'scholars.form.errors.nameRequired';
  if (form.email_public && !/^\S+@\S+\.\S+$/.test(form.email_public)) {
    return 'scholars.form.errors.emailInvalid';
  }
  return null;
}

// Submit a new scholar profile.
// - selfClaim=true → set profile_id = userId so future edits don't need
//   re-approval (RLS allows direct UPDATE on rows where profile_id = auth.uid).
// - asAdmin → status='published' immediately; otherwise 'pending'.
export async function createScholar(form, { authorId, selfClaim, asAdmin }) {
  const row = {
    ...normalize(form),
    profile_id: selfClaim ? authorId : null,
    status: asAdmin ? 'published' : 'pending',
    submitted_by: authorId,
  };
  const { data, error } = await supabase
    .from('scholars')
    .insert(row)
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

// Direct update — works for admins (full access policy) and self-managed
// scholars where auth.uid() = profile_id (spec §6.2).
export async function updateScholar(id, form) {
  const { data, error } = await supabase
    .from('scholars')
    .update(normalize(form))
    .eq('id', id)
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteScholar(id) {
  const { error } = await supabase.from('scholars').delete().eq('id', id);
  if (error) throw error;
}

// Build the "View bibliography" link for a scholar's row. Uses the stored
// link if any, otherwise auto-derives a filter URL by surname.
export function bibliographyLinkFor(scholar) {
  if (scholar.bibliography_link) return scholar.bibliography_link;
  const surname = scholar.name?.split(',')[0]?.trim() || scholar.name;
  if (!surname) return null;
  return `/bibliography?author=${encodeURIComponent(surname)}`;
}

// Filter / sort helpers used by the page.
export function filterScholars(scholars, { country, university, query }) {
  const q = (query ?? '').trim().toLowerCase();
  return scholars.filter((s) => {
    if (country && s.country !== country) return false;
    if (university && s.university !== university) return false;
    if (q) {
      const haystack = [s.name, s.university, s.department, s.position]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

export function sortScholars(scholars, mode) {
  const out = [...scholars];
  switch (mode) {
    case 'university':
      out.sort((a, b) => (a.university ?? '').localeCompare(b.university ?? '') || a.name.localeCompare(b.name));
      break;
    case 'country':
      out.sort((a, b) => (a.country ?? '').localeCompare(b.country ?? '') || a.name.localeCompare(b.name));
      break;
    case 'name':
    default:
      out.sort((a, b) => a.name.localeCompare(b.name));
      break;
  }
  return out;
}

export function deriveScholarFacets(scholars) {
  const countries = new Map();
  const universities = new Map();
  for (const s of scholars) {
    if (s.country) countries.set(s.country, (countries.get(s.country) ?? 0) + 1);
    if (s.university) universities.set(s.university, (universities.get(s.university) ?? 0) + 1);
  }
  return {
    countries: [...countries.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name)),
    universities: [...universities.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name)),
  };
}
